import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StoreBadges } from '../../components/ui/StoreBadges';
import { STAFF_APP_LINKS } from '../../config/app-links';
import { staffApi } from '../../api/staff';
import { formatProfessionalRange } from '../../config/support-pricing';
import './staff.css';

const quickStartSteps = [
  'Entra en Disponibles y revisa los tickets pendientes.',
  'Pulsa Aceptar en uno: se te asigna y abres el chat automáticamente.',
  'Responde al cliente desde el chat (llega por el mismo canal: web, WhatsApp o email).',
  'Cuando termines, pulsa Resolver e indica las horas facturables trabajadas.',
  'Revisa tus ganancias en la pestaña Ganancias.',
];

const clientChannels = [
  {
    title: 'Chat web',
    description:
      'El cliente abre el chat desde la web (botón flotante o página de contacto), escribe su nombre, email y mensaje. Se crea un ticket pendiente. Si no tiene cuenta Hosty, recibe un PIN de 6 dígitos para continuar en otro dispositivo.',
  },
  {
    title: 'WhatsApp',
    description:
      'Un mensaje entrante crea un ticket en la misma cola. Tus respuestas desde el panel se envían de vuelta por WhatsApp.',
  },
  {
    title: 'Email',
    description:
      'Al aceptar un ticket con email del cliente, se le envía un correo con enlace al chat y PIN (si es invitado). El cliente puede responder al hilo [Hosty #123] y su mensaje aparece en el ticket. Tus respuestas también llegan por email.',
  },
];

const clientLinkAndInstallation = [
  {
    title: 'Vincular cliente',
    description:
      'Desde Acciones → Vincular cliente (o Cambiar cuenta vinculada), busca la cuenta del cliente por email y asóciala al ticket. Así el cobro al resolver se carga automáticamente a su tarjeta guardada, sin enviar ningún enlace de pago.',
  },
  {
    title: 'Datos de instalación',
    description:
      'Solo en tickets de categoría instalación (presencial o remota). Recoge nombre del negocio y dirección hablando con el cliente por el chat y guárdalos en Acciones → Datos de instalación.',
  },
];

const ticketStates = [
  { state: 'Pendiente', meaning: 'Esperando que un agente lo acepte. Aparece en Disponibles.' },
  { state: 'Asignado / En curso', meaning: 'Lo has aceptado tú. Aparece en Mis tickets y puedes chatear.' },
  { state: 'Resuelto', meaning: 'Cerrado por ti. El cliente puede valorar la atención (1–5 estrellas).' },
];

const priorityLevels = [
  { level: 'Urgente', meaning: 'Incidencia crítica. Aparece arriba en la cola, con etiqueta roja y borde destacado.' },
  { level: 'Alta', meaning: 'Requiere atención pronto, pero sin la urgencia máxima.' },
  { level: 'Normal', meaning: 'Prioridad por defecto al crear el ticket.' },
  { level: 'Baja', meaning: 'Puede esperar sin problema.' },
];

const tips = [
  'Responde con claridad y en el idioma del cliente. Los mensajes de sistema (centro) informan de asignación y cierre.',
  'Si un invitado cambia de ordenador, comparte el PIN o el enlace del correo de asignación.',
  'Las horas facturables al resolver son las que trabajaste tú; determinan tu remuneración como agente.',
  'En Disponibles, los tickets urgentes salen primero. Mira la etiqueta roja URGENTE y el contador bajo el título.',
  'Si un ticket pendiente es crítico, márcalo como urgente desde la cola (sin necesidad de aceptarlo antes).',
  'Los mensajes nuevos llegan en tiempo real; si no ves uno, el chat se actualiza solo cada pocos segundos.',
];

const faq = [
  {
    q: '¿Puedo tener varios tickets a la vez?',
    a: 'Sí. Los que has aceptado aparecen en Mis tickets. Puedes alternar entre chats abriendo cada ticket.',
  },
  {
    q: '¿Qué pasa si el cliente no tiene email?',
    a: 'El chat web sigue funcionando en el navegador. Sin email no hay puente por correo con el cliente.',
  },
  {
    q: '¿Cómo se calculan mis ganancias?',
    a: 'Al resolver indicas las horas trabajadas. La tarifa depende de tu rol (técnico o team lead) y de si el ticket es estándar o urgente. Todo se resume en Ganancias.',
  },
  {
    q: '¿Cómo marco un ticket como urgente?',
    a: 'Los tickets nuevos llegan como Normal. En Disponibles puedes cambiar la prioridad con el desplegable o el botón «Marcar urgente» antes de aceptarlo. Una vez asignado, también puedes cambiarla desde el chat.',
  },
];

const sectionLinks = [
  { id: 'conexion-remota', label: 'Conexión remota' },
  { id: 'inicio-rapido', label: 'Inicio rápido' },
  { id: 'como-llegan', label: 'Cómo llegan los clientes' },
  { id: 'vincular-instalacion', label: 'Vincular cliente e instalación' },
  { id: 'panel', label: 'Secciones del panel' },
  { id: 'prioridad', label: 'Prioridad y urgencia' },
  { id: 'atender', label: 'Atender un ticket' },
  { id: 'cerrar', label: 'Cerrar un ticket' },
  { id: 'ganancias', label: 'Ganancias' },
  { id: 'app-movil', label: 'App móvil' },
  { id: 'consejos', label: 'Consejos' },
  { id: 'faq', label: 'Preguntas frecuentes' },
];

export function StaffHelpPage() {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadRemote = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      await staffApi.downloadRemoteClient();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'No se pudo descargar el instalador.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="staff-help">
      <header className="staff-page-header staff-help__header">
        <div>
          <h1>Manual de soporte</h1>
          <p className="staff-help__intro">
            Guía para agentes nuevos: cómo funciona el sistema de soporte técnico de Hosty y qué
            hacer en cada paso.
          </p>
        </div>
        <Link to="/staff" className="staff-help__cta">
          Ir a Disponibles →
        </Link>
      </header>

      <nav className="staff-help__nav" aria-label="Secciones del manual">
        {sectionLinks.map((item) => (
          <a key={item.id} href={`#${item.id}`} className="staff-help__nav-link">
            {item.label}
          </a>
        ))}
      </nav>

      <div className="staff-help__grid">
        <div id="conexion-remota">
        <Card
          title="Conexión remota (Hosty Remote)"
          subtitle="La herramienta para conectarte al equipo del cliente y darle soporte"
        >
          <p style={{ marginTop: 0 }}>
            Instala <strong>Hosty Remote Staff</strong> en tu equipo. Es la versión de agente: te
            deja introducir la ID que el cliente te lee por teléfono y conectarte a su TPV cuando él
            lo acepta.
          </p>
          <ol className="staff-help__steps">
            <li>Descarga e instala el programa (solo la primera vez).</li>
            <li>Pide al cliente que abra Hosty Remote y te dicte su ID.</li>
            <li>Introduce la ID, conéctate y espera a que el cliente pulse Aceptar.</li>
          </ol>
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <Button type="button" onClick={handleDownloadRemote} loading={downloading}>
              Descargar Hosty Remote Staff
            </Button>
            <span className="muted" style={{ fontSize: '0.85rem' }}>
              Windows · Solo para agentes
            </span>
          </div>
          {downloadError ? (
            <p className="staff-help__note" role="alert" style={{ color: 'var(--color-danger, #c0392b)' }}>
              {downloadError}
            </p>
          ) : null}
        </Card>
        </div>

        <div id="inicio-rapido">
        <Card title="Inicio rápido" subtitle="Tu primer ticket en 6 pasos">
          <ol className="staff-help__steps">
            {quickStartSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </Card>
        </div>

        <Card
          title="Flujo general"
          subtitle="De la consulta del cliente a tu remuneración"
          className="staff-help__flow-card"
        >
          <div className="staff-help__flow" role="img" aria-label="Flujo del soporte Hosty">
            <div className="staff-help__flow-step">
              <span className="staff-help__flow-num">1</span>
              <strong>Cliente contacta</strong>
              <span>Chat web, WhatsApp o email</span>
            </div>
            <div className="staff-help__flow-arrow" aria-hidden="true">
              →
            </div>
            <div className="staff-help__flow-step">
              <span className="staff-help__flow-num">2</span>
              <strong>Cola de tickets</strong>
              <span>Estado: Pendiente</span>
            </div>
            <div className="staff-help__flow-arrow" aria-hidden="true">
              →
            </div>
            <div className="staff-help__flow-step">
              <span className="staff-help__flow-num">3</span>
              <strong>Tú aceptas</strong>
              <span>Chat + notificación al cliente</span>
            </div>
            <div className="staff-help__flow-arrow" aria-hidden="true">
              →
            </div>
            <div className="staff-help__flow-step">
              <span className="staff-help__flow-num">4</span>
              <strong>Resuelves</strong>
              <span>Horas facturables + valoración</span>
            </div>
            <div className="staff-help__flow-arrow" aria-hidden="true">
              →
            </div>
            <div className="staff-help__flow-step">
              <span className="staff-help__flow-num">5</span>
              <strong>Ganancias</strong>
              <span>Resumen en tu panel</span>
            </div>
          </div>
        </Card>

        <div id="como-llegan">
        <Card
          title="Cómo llegan los clientes"
          subtitle="Todos los canales convergen en la misma cola de tickets"
        >
          <ul className="staff-help__channel-list">
            {clientChannels.map((channel) => (
              <li key={channel.title}>
                <strong>{channel.title}</strong>
                <p>{channel.description}</p>
              </li>
            ))}
          </ul>
        </Card>
        </div>

        <div id="vincular-instalacion">
        <Card
          title="Vincular cliente e instalación"
          subtitle="Cómo asegurar el cobro y coordinar instalaciones"
        >
          <ul className="staff-help__channel-list">
            {clientLinkAndInstallation.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <p>{item.description}</p>
              </li>
            ))}
          </ul>
        </Card>
        </div>

        <div id="panel">
        <Card title="Secciones del panel" subtitle="Qué hace cada pestaña del menú">
          <dl className="staff-help__definitions">
            <div>
              <dt>
                <Link to="/staff">Disponibles</Link>
              </dt>
              <dd>
                Tickets pendientes que nadie ha aceptado aún. Los urgentes aparecen primero, con
                etiqueta roja. Aquí puedes marcar la prioridad antes de aceptar.
              </dd>
            </div>
            <div>
              <dt>
                <Link to="/staff/mis-tickets">Mis tickets</Link>
              </dt>
              <dd>
                Tickets que ya has aceptado y siguen abiertos. Entra al chat desde cada tarjeta.
              </dd>
            </div>
            <div>
              <dt>
                <Link to="/staff/ganancias">Ganancias</Link>
              </dt>
              <dd>
                Resumen de tickets resueltos, valoraciones, ventas y liquidaciones del mes.
              </dd>
            </div>
            <div>
              <dt>
                <Link to="/staff/referidos">Referidos</Link>
              </dt>
              <dd>
                Tu código de referido, niveles (Bronce a Diamante) y bonificaciones por hitos de clientes activos.
              </dd>
            </div>
            <div>
              <dt>Manual</dt>
              <dd>Esta guía. Puedes volver cuando lo necesites.</dd>
            </div>
          </dl>
        </Card>
        </div>

        <div id="prioridad">
        <Card
          title="Prioridad y urgencia"
          subtitle="Cómo identificar y marcar tickets urgentes en la cola"
        >
          <p>
            Los tickets nuevos llegan con prioridad <strong>Normal</strong>. Ningún cliente puede
            marcarlos como urgentes al contactar: lo hace el agente durante el triaje.
          </p>
          <h3 className="staff-help__subheading">Cómo verlos en Disponibles</h3>
          <ul className="staff-help__bullets">
            <li>
              Los urgentes salen <strong>arriba del todo</strong> en la lista.
            </li>
            <li>
              Llevan etiqueta roja <strong>URGENTE</strong>, badge de prioridad y borde destacado en
              la tarjeta.
            </li>
            <li>El encabezado muestra cuántos tickets urgentes hay en cola.</li>
          </ul>
          <h3 className="staff-help__subheading">Cómo marcarlos</h3>
          <ol className="staff-help__steps">
            <li>
              En <Link to="/staff">Disponibles</Link>, usa el desplegable de prioridad en la tarjeta
              del ticket o pulsa <strong>Marcar urgente</strong>.
            </li>
            <li>
              No hace falta aceptar el ticket antes: cualquier agente puede clasificar tickets
              pendientes.
            </li>
            <li>
              Una vez asignado, cambia la prioridad desde el selector del chat si hace falta.
            </li>
          </ol>
          <h3 className="staff-help__subheading">Niveles de prioridad</h3>
          <table className="staff-help__table">
            <thead>
              <tr>
                <th scope="col">Prioridad</th>
                <th scope="col">Cuándo usarla</th>
              </tr>
            </thead>
            <tbody>
              {priorityLevels.map((row) => (
                <tr key={row.level}>
                  <td>{row.level}</td>
                  <td>{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="staff-help__note">
            Marcar un ticket como <strong>Urgente</strong> aplica la tarifa urgente en tus ganancias
            al resolver.
          </p>
        </Card>
        </div>

        <div id="atender">
        <Card title="Atender un ticket" subtitle="Desde aceptar hasta responder">
          <h3 className="staff-help__subheading">Estados del ticket</h3>
          <table className="staff-help__table">
            <thead>
              <tr>
                <th scope="col">Estado</th>
                <th scope="col">Significado</th>
              </tr>
            </thead>
            <tbody>
              {ticketStates.map((row) => (
                <tr key={row.state}>
                  <td>{row.state}</td>
                  <td>{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="staff-help__subheading">En el chat</h3>
          <ul className="staff-help__bullets">
            <li>
              <strong>Mensajes del cliente</strong> — a la izquierda. Llegan por web, WhatsApp o
              email según el origen del ticket.
            </li>
            <li>
              <strong>Tus respuestas</strong> — a la derecha. Se envían por el mismo canal
              automáticamente.
            </li>
            <li>
              <strong>Mensajes de sistema</strong> — en el centro. Avisan de asignación o cierre.
            </li>
          </ul>
          <p className="staff-help__note">
            Al aceptar un ticket, el cliente recibe un aviso (push en app, email o mensaje en el
            chat). Si es invitado por web, también recibe un PIN para reanudar la conversación en
            otro dispositivo.
          </p>
        </Card>
        </div>

        <div id="cerrar">
        <Card title="Cerrar un ticket" subtitle="Resolver y registrar tu trabajo">
          <p>
            Cuando hayas terminado la atención, pulsa <strong>Resolver</strong> en el chat:
          </p>
          <ol className="staff-help__steps">
            <li>
              Indica las <strong>horas facturables</strong> que has dedicado (entre 0,25 y 24).
            </li>
            <li>Añade notas de resolución si quieres dejar constancia interna (opcional).</li>
            <li>Confirma. El ticket pasa a Resuelto y el cliente puede valorarte del 1 al 5.</li>
          </ol>
          <p className="staff-help__note">
            Tarifa orientativa al resolver: estándar {formatProfessionalRange('standard')} y urgente{' '}
            {formatProfessionalRange('urgent')} según tu rol (técnico o team lead). El panel te
            muestra la tarifa aplicable según la prioridad del ticket.
          </p>
        </Card>
        </div>

        <div id="ganancias">
        <Card title="Ganancias y liquidaciones" subtitle="Dónde ver lo que has generado">
          <p>
            En <Link to="/staff/ganancias">Ganancias</Link> encuentras el resumen del mes actual o
            anterior:
          </p>
          <ul className="staff-help__bullets">
            <li>Tickets resueltos y horas facturadas.</li>
            <li>Valoración media de los clientes.</li>
            <li>Comisiones por ventas de hardware o servicios registradas manualmente (si aplica).</li>
            <li>Historial de liquidaciones (pendiente, en proceso o pagado).</li>
          </ul>
          <p className="staff-help__note">
            El programa de referidos (código, niveles y hitos de pago único) está en{' '}
            <Link to="/staff/referidos">Referidos</Link>. Tu nivel por clientes activos referidos va
            de Bronce (0–9) a Diamante (50+). Las bonificaciones por hitos son 100 €, 300 € y 800 €.
          </p>
        </Card>
        </div>

        <div id="app-movil">
        <Card
          title="App móvil Hosty Staff"
          subtitle="Gestiona tickets desde el móvil, estés donde estés"
        >
          <p className="muted" style={{ marginTop: 0 }}>
            Disponible para iOS y Android con tu mismo usuario de agente.
          </p>
          <StoreBadges
            appName="Hosty Staff"
            appStoreUrl={STAFF_APP_LINKS.appStore}
            playStoreUrl={STAFF_APP_LINKS.playStore}
          />
        </Card>
        </div>

        <div id="consejos">
        <Card title="Consejos prácticos">
          <ul className="staff-help__bullets">
            {tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </Card>
        </div>

        <div id="faq">
        <Card title="Preguntas frecuentes">
          <dl className="staff-help__faq">
            {faq.map((item) => (
              <div key={item.q}>
                <dt>{item.q}</dt>
                <dd>{item.a}</dd>
              </div>
            ))}
          </dl>
        </Card>
        </div>
      </div>
    </div>
  );
}
